import {TranslocoService} from '@jsverse/transloco';
import {Injectable} from '@angular/core';
import {AnimalOriginReasonForTransfer} from 'src/app/modules/animals/models/animal-origin-reason-for-transfer.enum';
import {AnimalWhereaboutsType} from 'src/app/modules/animals/models/animal-wherabout-types.enum';
import {firstValueFrom} from 'rxjs';

@Injectable({
  providedIn: 'root'
})

export class HelperService {
  private loadedLangs = new Set<string>();

  constructor(
    private readonly _translateService: TranslocoService,
  ) {
  }

  getEnumOptions<T extends Record<string, string>, U extends {
    name: string,
    value: string
  }>(enumVariable: T, translationKey: string, notEmptyValue?: boolean): U[] {
    const options: U[] = [];
    if (!notEmptyValue) {
      options.push({name: this._translateService.translateObject('noValue'), value: null as unknown} as U);
    }

    for (const key in enumVariable) {
      if (Object.prototype.hasOwnProperty.call(enumVariable, key)) {
        const value = enumVariable[key].toLowerCase();
        const name = this._translateService.translateObject(`${translationKey}.${value}`);
        options.push({name, value} as U);
      }
    }

    return options.sort((a, b) => a.name.localeCompare(b.name));
  }

  getDateStringByLang(): string {
    if (this._translateService.getActiveLang() === 'en') {
      return 'yyyy-MM-dd';
    } else {
      return 'dd.MM.yyyy';
    }
  }

  getSalutationOptions(): { name: string, value: string | null }[] {
    return [
      {name: this._translateService.translate('salutation.none'), value: null},
      {name: this._translateService.translate('salutation.male'), value: 'male'},
      {name: this._translateService.translate('salutation.female'), value: 'female'},
      {name: this._translateService.translate('salutation.diverse'), value: 'diverse'},
      {name: this._translateService.translate('salutation.family'), value: 'family'},
    ].sort((a, b) => a.name.localeCompare(b.name))
  }

  getTitleOptions(): { name: string, value: string | null }[] {
    return [
      {name: this._translateService.translate('title.none'), value: null},
      {name: this._translateService.translate('title.Dr.'), value: 'Dr.'},
      {name: this._translateService.translate('title.Prof.'), value: 'Prof.'},
      {name: this._translateService.translate('title.Dipl.-Ing.'), value: 'Dipl.-Ing.'},
      {name: this._translateService.translate('title.Mag.'), value: 'Mag.'},
      {name: this._translateService.translate('title.B.Sc.'), value: 'B.Sc.'},
      {name: this._translateService.translate('title.M.Sc.'), value: 'M.Sc.'},
      {name: this._translateService.translate('title.PhD'), value: 'PhD'},
    ]
  }


  determineDateOfReceipt(originalDate: string | Date, newDate: string | Date): Date {
    const originalNormalized = this.normalizeDate(new Date(originalDate));
    const newNormalized = this.normalizeDate(new Date(newDate));
    return originalNormalized.getTime() === newNormalized.getTime() ? originalNormalized : newNormalized;
  }

  normalizeDate(date: Date): Date {
    const normalized = new Date(date.getTime());
    normalized.setMinutes(normalized.getMinutes() - normalized.getTimezoneOffset());
    return normalized;
  }

  toAbsoluteUrl(raw?: string | null): string | null {
    if (!raw) return null;
    const url = raw.trim();
    if (!url) return null;

    const hasScheme = /^[a-zA-Z][\w+.-]*:/.test(url);
    if (hasScheme) return url;

    return `https://${url.replace(/^\/+/, '')}`;
  }

  translateAnimalCategory(category: string): string {
    const translationKey = `animal.category.${category}`;
    const translated = this._translateService.translate(translationKey);

    return translated !== translationKey ? translated : category;
  }

  translateOriginType(origin: string): string {
    const entry = Object.entries(AnimalOriginReasonForTransfer)
      .find(([, value]) => value === origin);

    const key = entry?.[0];

    if (!key) return origin;

    const translationKey = `animalAdd.origin.reasonForTransferSelect.${key}`;
    const translated = this._translateService.translate(translationKey);

    return translated !== translationKey ? translated : origin;
  }

  translateWheraboutType(whereAbout: string): string {
    const entry = Object.entries(AnimalWhereaboutsType)
      .find(([, value]) => value === whereAbout);

    const key = entry?.[0];

    if (!key) return whereAbout;

    const translationKey = `animal.whereabout.${key}`;
    const translated = this._translateService.translate(translationKey);

    return translated !== translationKey ? translated : whereAbout;
  }

  sanitizeOldBookNumber(value: string): string {
    return value.replace(/[^a-zA-Z0-9\-\/]/g, '');
  }

  async ensureLang(lang: string): Promise<void> {
    if (this.loadedLangs.has(lang)) return;
    await this.loadLang(lang);
  }

  async ensureActiveLangLoaded(): Promise<void> {
    await this.ensureLang(this._translateService.getActiveLang());
  }

  private async loadLang(lang: string): Promise<void> {
    if (this.loadedLangs.has(lang)) return;

    await firstValueFrom(this._translateService.load(lang));
    this.loadedLangs.add(lang);
  }
}
